import { useEffect, useState } from 'react';
import { geocodeAddressWithCache, chunk } from '@/utils/geocoding';
import type { PropertyLocation } from '@/components/map/PropertyMap';

interface Property {
  id: string | number;
  title: string;
  price: string;
  image: string | string[];
}

interface Imovel {
  id: string;
  endereco_logradouro: string | null;
  endereco_numero: string | null;
  endereco_bairro: string | null;
  endereco_cidade: string | null;
  endereco_uf: string | null;
  latitude: number | null;
  longitude: number | null;
}

const BATCH_SIZE = 3; // Processar 3 por vez
const RATE_LIMIT_DELAY = 1000; // 1 segundo entre batches

// Coordenadas corretas do centro de cada cidade
const CITY_COORDS: Record<string, { lat: number; lng: number }> = {
  'Campinas': { lat: -22.9070, lng: -47.0629 }, // Centro de Campinas (Catedral)
  'Piracicaba': { lat: -22.7341, lng: -47.6488 },
  'Valinhos': { lat: -22.9706, lng: -46.9964 },
  'Vinhedo': { lat: -23.0299, lng: -46.9753 },
  'Sumaré': { lat: -22.8219, lng: -47.2669 },
  'Hortolândia': { lat: -22.8582, lng: -47.2200 },
  'Indaiatuba': { lat: -23.0904, lng: -47.2178 },
  'Jundiaí': { lat: -23.1864, lng: -46.8842 },
};

const FALLBACK_COORDS = { lat: -22.9070, lng: -47.0629 }; // Centro de Campinas

export function useGeocodedProperties(
  properties: Property[],
  imoveis: Imovel[] | undefined
): { locations: PropertyLocation[]; isGeocoding: boolean; progress: string } {
  const [locations, setLocations] = useState<PropertyLocation[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [progress, setProgress] = useState('');

  useEffect(() => {
    if (!properties.length || !imoveis) {
      setLocations([]);
      setProgress('');
      return;
    }

    let isCancelled = false;

    const geocodeAllProperties = async () => {
      setIsGeocoding(true);
      const allLocations: PropertyLocation[] = [];
      
      // Separar imóveis com e sem coordenadas
      const withCoords: Array<{ property: Property; imovel: Imovel }> = [];
      const needsGeocode: Array<{ property: Property; imovel: Imovel }> = [];

      for (const property of properties) {
        const imovel = imoveis.find(i => i.id === property.id);
        if (!imovel) continue;

        if (imovel.latitude && imovel.longitude) {
          withCoords.push({ property, imovel });
        } else if (
          imovel.endereco_logradouro &&
          imovel.endereco_bairro &&
          imovel.endereco_cidade &&
          imovel.endereco_uf
        ) {
          needsGeocode.push({ property, imovel });
        } else {
          // Sem coordenadas e sem endereço completo - usar fallback
          withCoords.push({ property, imovel });
        }
      }

      console.log(`📍 ${withCoords.length} com coordenadas, ${needsGeocode.length} para geocodificar`);

      // 1️⃣ Processar imóveis com coordenadas PRIMEIRO (instantâneo)
      for (const { property, imovel } of withCoords) {
        if (isCancelled) return;

        // Se não tem coordenadas, usar coordenadas da cidade ou fallback geral
        const cityCoords = imovel.endereco_cidade ? CITY_COORDS[imovel.endereco_cidade] : null;
        const latitude = imovel.latitude || cityCoords?.lat || FALLBACK_COORDS.lat;
        const longitude = imovel.longitude || cityCoords?.lng || FALLBACK_COORDS.lng;

        // Log detalhado
        if (!imovel.latitude) {
          console.log(`📍 Usando fallback para ${property.id}:`, {
            cidade: imovel.endereco_cidade,
            coordenadas: { lat: latitude, lng: longitude },
            tipo: cityCoords ? 'cidade' : 'geral'
          });
        }

        allLocations.push({
          id: String(property.id),
          position: [latitude, longitude],
          title: property.title,
          price: property.price,
          image: Array.isArray(property.image) ? property.image[0] : property.image || '/img-default.jpg',
        });
      }

      // Atualizar mapa com os que já têm coordenadas
      if (!isCancelled) {
        setLocations([...allLocations]);
        setProgress(`${withCoords.length}/${properties.length} imóveis`);
      }

      // 2️⃣ Geocodificar o resto em paralelo (batches)
      if (needsGeocode.length === 0) {
        setIsGeocoding(false);
        setProgress('');
        return;
      }

      const batches = chunk(needsGeocode, BATCH_SIZE);
      
      for (let i = 0; i < batches.length; i++) {
        if (isCancelled) return;

        const batch = batches[i];
        
        // Processar batch em paralelo
        const results = await Promise.all(
          batch.map(async ({ property, imovel }) => {
            const result = await geocodeAddressWithCache(
              imovel.endereco_logradouro!,
              imovel.endereco_numero || '',
              imovel.endereco_bairro!,
              imovel.endereco_cidade!,
              imovel.endereco_uf!
            );

            // Se geocoding falhar, usar coordenadas da cidade ou fallback geral
            const cityCoords = imovel.endereco_cidade ? CITY_COORDS[imovel.endereco_cidade] : null;
            const latitude = result?.latitude || cityCoords?.lat || FALLBACK_COORDS.lat;
            const longitude = result?.longitude || cityCoords?.lng || FALLBACK_COORDS.lng;

            // Log detalhado com ID do imóvel
            if (result) {
              console.log(`✅ ${property.id} - ${imovel.endereco_bairro}, ${imovel.endereco_cidade}`);
            } else {
              const fallbackType = cityCoords ? `cidade ${imovel.endereco_cidade}` : 'Campinas geral';
              console.warn(`⚠️ ${property.id} - Usando fallback (${fallbackType}):`, {
                endereco: `${imovel.endereco_logradouro}, ${imovel.endereco_bairro}`,
                coordenadas: { lat: latitude, lng: longitude }
              });
            }

            return {
              id: String(property.id),
              position: [latitude, longitude] as [number, number],
              title: property.title,
              price: property.price,
              image: Array.isArray(property.image) ? property.image[0] : property.image || '/img-default.jpg',
            };
          })
        );

        // Adicionar resultados do batch
        allLocations.push(...results);
        
        // Atualizar progressivamente
        if (!isCancelled) {
          setLocations([...allLocations]);
          const total = withCoords.length + (i + 1) * BATCH_SIZE;
          setProgress(`${Math.min(total, properties.length)}/${properties.length} imóveis`);
        }

        // Rate limit entre batches (exceto no último)
        if (i < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, RATE_LIMIT_DELAY));
        }
      }

      if (!isCancelled) {
        setIsGeocoding(false);
        setProgress('');
      }
    };

    geocodeAllProperties();

    return () => {
      isCancelled = true;
    };
  }, [properties, imoveis]);

  return { locations, isGeocoding, progress };
}
